import Panel from "@/components/Panel";
import EinHead from "@/components/icons/EinHead";
import NextButton from "@/components/NextButton";
import PropertyDetails from "@/components/PropertyDetails";
import Features from "@/components/Features";
import StepTitles from "@/components/StepTitles";
import {useEffect, useRef} from "react";
import {useAppState} from "@/store";
import {useValidation} from "@/validation";
import {apiUrl, camelCaseKeys, dataLayerPush, getGaClientId, getGclid} from "@/helpers";
import StepFooter from "@/components/StepFooter";
import {API_ENDPOINTS} from "@/constants";
import {useApi} from "@/api";
import {toast} from "react-toastify";
import ChoiceMulti from "@/components/fields/ChoiceMulti";

export default function LocationDetails({goToStep}) {

    const {
        state,
        setState
    } = useAppState()
    const {preSubmitValidate} = useValidation()
    const {api, setLoading} = useApi()

    const content = window.ein?.content?.step_1 || {}

    const {
        title,
        secondary_title,
        tertiary_title,
        from,
        to,
        button_label
    } = content

    const form = useRef(null)

    const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)

        try {

            preSubmitValidate()

            const from = camelCaseKeys(state.from)
            const to = camelCaseKeys(state.to)

            const data = {
                from,
                to,
                fullness: state.fullness.how_packed
            }


            const gclid = getGclid()

            if (gclid) {
                data.gclid = gclid
            }

            const clientId = getGaClientId()

            if (clientId) {
                data.externalId = clientId
            }

            const response = await api.post(apiUrl(API_ENDPOINTS.address), data)

            setState((s) => {
                s.requestId = response.requestId
                s.branch = response.branch
                s.inestimable['address'] = response.inestimable?.status  ? response.inestimable?.reasons : false
            })


            await dataLayerPush('book-location-submit', {
                ['book-location-from-zip']: state.from.zip,
                ['book-location-from-type']: state.from.propertyType,
                ['book-location-from-size']: state.from.size,
                ['book-location-to-zip']: state.to.zip,
                ['book-location-to-type']: state.to.propertyType
            })

            goToStep(2)
        } catch (e) {
            toast.error(e.message)
        }
        setLoading(false)
    }

    useEffect(() => {
        dataLayerPush('book-start-view', {}).then()
    }, [])

    return (
        <form className={"grid grid-cols-4 lg:grid-cols-12 gap-x-[20px] lg:gap-x-[32px] gap-y-[32px] lg:gap-y-[40px]"} onSubmit={handleSubmit} ref={form}>
            <div className="lg:col-span-3 hidden lg:!flex items-center">
                <EinHead className={"ml-[-16px] rotate-[-8deg]"}/>
            </div>
            <div className="col-span-4 lg:col-span-9 flex flex-col justify-center text-center">
                <div className={"smart-tag mx-auto mb-[24px] lg:mb-[32px]"}>
                    🧠 Smart Move System™ Activated
                </div>
                <StepTitles title={title} secondaryTitle={secondary_title} tertiaryTitle={tertiary_title}/>
            </div>
            <div className="col-span-4 lg:col-span-12">
                <Features/>
            </div>
            <div className="col-span-4 lg:col-span-6">
                <Panel>
                    <div className="rounded-full bg-brand-orange heading-h6 text-white absolute top-[28px] left-[28px] size-[32px] flex items-center justify-center">
                        1
                    </div>
                    {from.title && (<div className="heading-h3 lg:mb-[8px] text-center">
                            {from.title}
                        </div>)}
                    {from.subtitle && (<div className="text-brand-grey text-center">
                            {from.subtitle}
                        </div>)}
                    <div className={"grid grid-cols-1 gap-[24px] lg:gap-[32px] mt-[24px]"}>
                        <PropertyDetails stateKey={'from'}/>
                    </div>
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-6">
                <Panel>
                    <div className="rounded-full bg-brand-orange heading-h6 text-white absolute top-[28px] left-[28px] size-[32px] flex items-center justify-center">
                        2
                    </div>
                    {to.title && (<div className="heading-h3 lg:mb-[8px] text-center">
                            {to.title}
                        </div>)}
                    {to.subtitle && (<div className="text-brand-grey text-center">
                            {to.subtitle}
                        </div>)}
                    <div className={"grid grid-cols-1 gap-[24px] lg:gap-[32px] mt-[24px]"}>
                        <PropertyDetails stateKey={'to'}/>
                    </div>
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-12">
                <Panel passive={true}>
                    <div className="heading-h4 text-center">
                        How packed is your place, honestly?
                    </div>
                    <div className={"text-center italic text-brand-grey text-sm mt-[4px]"}>
                        Same square footage can mean wildly different amounts of stuff, this information helps us get it right.
                    </div>
                    <ChoiceMulti
                        stateKey={"fullness"}
                        name={"how_packed"}
                        labels={[
                            <>Pretty minimal</>,
                            <>About average</>,
                            <>Packed to the gills</>
                        ]}
                        values={[
                            "minimal",
                            "average",
                            "packed"
                        ]}
                    />
                </Panel>
            </div>
            <div className="col-span-4 lg:col-span-12 flex justify-center">
                <NextButton text={button_label} disabled={false}/>
            </div>
            <div className="col-span-4 lg:col-span-12">
                <StepFooter/>
            </div>
        </form>)
}
